/**
 * Device-tier detection — the SINGLE source of truth for "which UX tier is this
 * device", so the phone-specific chrome (legibility boost, rail inversion,
 * bottom action bar, gutter bump) and the broader touch ergonomics (iPhone AND
 * iPad) branch off ONE place instead of scattering ad-hoc userAgent sniffing.
 *
 * Two tiers, both keyed off DEVICE-STABLE signals (never `userAgent`; never the
 * mutable `innerWidth`/`innerHeight` that a Stage-Manager-shrunk window could
 * change — see the tier-flip note in docs/IPHONE_IPAD_UX_STRATEGY.md §4):
 *
 *   • COMPACT (phone-class): the screen's SHORT edge in CSS px is `< 600`.
 *     iPhones top out near 440 (a Pro Max landscape height); the smallest iPad
 *     (mini) starts at 744 — 600 splits them with a wide margin and is
 *     orientation-independent. This is the EXACT signal `displayDpr` already
 *     trusts for the load-bearing WebKit-jetsam DPR cap, so we keep it verbatim
 *     and consolidate it here (displayDpr now imports {@link isCompactScreen}).
 *     Compact gets the chrome legibility boost, the inverted rails and the
 *     bottom action bar.
 *   • TOUCH: a coarse PRIMARY pointer OR compact. iPhone AND iPad (and any
 *     touch laptop). They get the touch ergonomics — side-aware panels, tap-to-
 *     show tooltips, larger tap-slop, haptics. A plain desktop with a mouse is
 *     NOT touch tier, so its UX is untouched.
 *
 * Deliberately NOT used as signals (per the research + stress-test):
 *   • `userAgent` — iPadOS spoofs a Mac UA; brittle.
 *   • `devicePixelRatio` — varies WITHIN both tiers.
 *   • `any-pointer: fine` to "harden" compact — REGRESSES an iPhone paired with
 *     a Bluetooth mouse / Switch Control out of phone tier. The plain short-edge
 *     test is more robust; we do not compose it with pointer media.
 *
 * Conservative by design: anything unmeasurable (no `window.screen`, headless
 * tests) reads as NOT compact / NOT touch, so desktop + iPad keep today's
 * behaviour and the CI suite is unaffected unless a test opts in.
 */

/**
 * Short-edge CSS-px boundary between phone-class (compact) and tablet/desktop.
 * iPhone Pro Max landscape height ≈ 440; iPad mini short edge = 744. 600 splits
 * them with margin. MIRRORED in the index.html inline `fit()` (it publishes
 * `phoneTier` onto `window.__stageFit` before the bundle loads) — keep in
 * lock-step; `tests/utils/deviceTier.indexHtmlMirror.test.ts` guards the value.
 */
export const PHONE_SHORT_EDGE_MAX = 600;

/**
 * True for a phone-class (compact) screen — the tight-memory, small-physical
 * tier. Keyed off the device-stable SHORT edge in CSS px. Unmeasurable →
 * `false` (treated as tablet/desktop) so headless tests and missing-`screen`
 * environments never misclassify into the phone branch.
 */
export function isCompactScreen(): boolean {
  if (typeof window === "undefined" || !window.screen) return false;
  const w = window.screen.width;
  const h = window.screen.height;
  if (!w || !h) return false;
  return Math.min(w, h) < PHONE_SHORT_EDGE_MAX;
}

/**
 * Back-compat alias — `displayDpr` historically called a private
 * `isPhoneClassScreen()`. Same logic, now shared. Prefer {@link isCompactScreen}
 * in new code.
 */
export const isPhoneClassScreen = isCompactScreen;

/**
 * True when the PRIMARY pointer is coarse (finger/stylus) — i.e. a real touch
 * device. Uses `matchMedia('(pointer: coarse)')` (the *primary* pointer, not
 * `any-pointer`) so a desktop with a touchscreen but a mouse as its main input
 * stays fine-pointer. Falsy/absent `matchMedia` (headless) → false.
 */
function hasCoarsePrimaryPointer(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  try {
    return window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}

/**
 * True for the TOUCH tier (iPhone + iPad + touch laptops): a coarse primary
 * pointer OR a compact screen. Compact is OR-ed in so a phone that (oddly)
 * reports a fine pointer — e.g. paired with a Bluetooth mouse — still counts as
 * touch. Desktop-with-mouse → false (untouched UX).
 */
export function isTouchTier(): boolean {
  return hasCoarsePrimaryPointer() || isCompactScreen();
}
